/**
 * uiux-renewal 상점 도메인 목 데이터 — Season 1 Box(§4.3~4.8)·Ultra Chase(§4.9)·
 * 팩 인벤토리(S-31) 표시용. 서버 상품 API 부재 상태의 화면 골격 전용이며,
 * 확률·잔여·시세류는 전부 mock 또는 null(자리 예약)이다.
 * TODO(server): 박스 상품 목록/상세 RPC, Ultra Chase 잔여 실시간 RPC,
 * 팩 인벤토리 RPC 연결 시 이 파일의 상수 전체를 서버 값으로 교체.
 */

// ---------------------------------------------------------------- Season 1 Box

export type BoxId = "hobby" | "blaster" | "retail";

export type SeasonBox = {
  id: BoxId;
  name: string;
  /** mock — 서버 상품 가격 미확정 (PRD Q7) */
  priceRp: number;
  /** mock — 박스 구성 수량 미확정 */
  packsPerBox: number;
  cardsPerPack: number;
  /** §3 SupplyCounter용 발행·잔여 — TODO(server): 실시간 잔여 RPC로 교체 */
  supplyTotal: number;
  supplyRemaining: number;
  /** §4.10 Guaranteed — 해당 박스에 반드시 포함 */
  guaranteed: string[];
  /** §4.10 Look for — 획득 가능하지만 보장하지 않음 (/1 카드 항상 여기) */
  lookFor: string[];
  /** §4.8 박스 전용 Parallel */
  exclusiveParallels: string[];
};

export const SEASON_BOXES: SeasonBox[] = [
  {
    id: "hobby",
    name: "Hobby Box",
    priceRp: 9_900, // mock
    packsPerBox: 12, // mock
    cardsPerPack: 8, // mock
    supplyTotal: 500, // mock
    supplyRemaining: 312, // mock
    guaranteed: [
      "Multiple Holo / Parallel cards",
      "Multiple Insert cards",
    ],
    lookFor: [
      "Numbered card (guarantee under review)",
      "Autograph card",
      "Asset-Linked card",
      "Hobby-exclusive Parallel",
      "Ultra Chase /1 card",
    ],
    exclusiveParallels: ["Gold Wave", "Black Pulse"],
  },
  {
    id: "blaster",
    name: "Blaster Box",
    priceRp: 3_900, // mock
    packsPerBox: 6, // mock
    cardsPerPack: 8, // mock
    supplyTotal: 2_000, // mock
    supplyRemaining: 1_408, // mock
    guaranteed: ["Set quantity of Holo / Parallel cards"],
    lookFor: [
      "Insert card",
      "Numbered card",
      "Autograph card (low odds)",
      "Asset-Linked card (low odds)",
      "Blaster-exclusive Parallel",
      "Ultra Chase /1 card",
    ],
    exclusiveParallels: ["Pink Wave", "Neon Burst"],
  },
  {
    id: "retail",
    name: "Retail Box",
    priceRp: 1_900, // mock
    packsPerBox: 3, // mock
    cardsPerPack: 8, // mock
    supplyTotal: 5_000, // mock
    supplyRemaining: 4_126, // mock
    guaranteed: ["Base & Holo core lineup"],
    lookFor: [
      "Insert card (limited)",
      "Numbered card (low odds)",
      "Autograph card (very low odds)",
      "Asset-Linked card (very low odds)",
      "Retail-exclusive Parallel",
      "Ultra Chase /1 card",
    ],
    exclusiveParallels: ["Red Ice", "Blue Spark"],
  },
];

export function findBox(boxId: string | undefined): SeasonBox | undefined {
  return SEASON_BOXES.find((box) => box.id === boxId);
}

/** §4.7 박스 위계 9축 비교 — 값은 스펙 정성 표현의 영문화(확정 수치 아님) */
export type BoxCompareAxis = {
  axis: string;
  values: Record<BoxId, string>;
  /** F-08 — 보장 셀=Guaranteed, 가능성 셀=Look for(/1은 항상 Look for) */
  marks?: Partial<Record<BoxId, "guaranteed" | "look-for">>;
};

const LOOK_FOR_ALL = {
  hobby: "look-for",
  blaster: "look-for",
  retail: "look-for",
} as const;

export const BOX_COMPARE_AXES: BoxCompareAxis[] = [
  { axis: "Price", values: { hobby: "Highest", blaster: "Mid", retail: "Lowest" } },
  { axis: "Total cards", values: { hobby: "Most", blaster: "Mid", retail: "Fewest" } },
  { axis: "Average value", values: { hobby: "Highest", blaster: "Mid", retail: "Lowest" } },
  {
    axis: "Rare-card expectation",
    values: { hobby: "Highest", blaster: "Mid", retail: "Lowest" },
  },
  {
    axis: "Numbered card",
    values: { hobby: "Guarantee under review", blaster: "Odds-based", retail: "Low odds" },
    marks: LOOK_FOR_ALL,
  },
  {
    axis: "Autograph",
    values: { hobby: "High odds", blaster: "Low odds", retail: "Very low odds" },
    marks: LOOK_FOR_ALL,
  },
  {
    axis: "Asset-Linked card",
    values: { hobby: "High odds", blaster: "Low odds", retail: "Very low odds" },
    marks: LOOK_FOR_ALL,
  },
  {
    axis: "Exclusive Parallel",
    values: {
      hobby: "Gold Wave · Black Pulse",
      blaster: "Pink Wave · Neon Burst",
      retail: "Red Ice · Blue Spark",
    },
  },
  {
    axis: "Ultra Chase",
    values: {
      hobby: "Available — not guaranteed",
      blaster: "Available — not guaranteed",
      retail: "Available — not guaranteed",
    },
    marks: LOOK_FOR_ALL, // /1은 항상 Look for
  },
];

// ---------------------------------------------------------------- Ultra Chase

/**
 * §4.9 Ultra Chase /1 카드 — lib/cards.ts supplyCap 1 티어(padparadscha_sapphire,
 * gold_vinyl) 기반. §4.9.4 공개 9항목 중 미확정 값은 null(자리 예약, 서버 값 표시).
 */
export type UltraChaseCard = {
  id: string;
  name: string;
  /** lib/cards.ts finish key — RarityBadge/finishMeta 재사용 */
  finish: "padparadscha_sapphire" | "gold_vinyl";
  chaseLevel: "Master Chase" | "Ultimate Chase";
  claimed: boolean;
  /** §4.9.4 공개 항목 — null = 상품 출시 전 서버 값 자리 예약 */
  disclosure: {
    benchmarkValue: string | null;
    linkedAsset: string | null;
    vault: string | null;
    settlement: string | null;
    kyc: string | null;
    fees: string | null;
    processingTime: string | null;
    burnCondition: string | null;
    buyback: string | null;
  };
};

export const ULTRA_CHASE_CARDS: UltraChaseCard[] = [
  {
    id: "s1-masterpiece-gold-vinyl",
    name: "Season 1 Masterpiece Gold Vinyl /1",
    finish: "gold_vinyl",
    chaseLevel: "Ultimate Chase",
    claimed: false,
    disclosure: {
      benchmarkValue: "~$1,000,000", // mock benchmark

      linkedAsset: null,
      vault: null,
      settlement: "Hold / Vault settlement / designated exchange / premium review",
      kyc: "Required before settlement",
      fees: null,
      processingTime: null,
      burnCondition: "Card is burned on asset settlement",
      buyback: null,
    },
  },
  {
    id: "s1-padparadscha-sapphire",
    name: "Padparadscha Sapphire /1",
    finish: "padparadscha_sapphire",
    chaseLevel: "Master Chase",
    claimed: true, // mock — 획득 완료 진열 케이스
    disclosure: {
      benchmarkValue: "~$500,000", // mock benchmark

      linkedAsset: null,
      vault: null,
      settlement: "Hold / Vault settlement / designated exchange / premium review",
      kyc: "Required before settlement",
      fees: null,
      processingTime: null,
      burnCondition: "Card is burned on asset settlement",
      buyback: null,
    },
  },
];

export function ultraChaseStats() {
  const total = ULTRA_CHASE_CARDS.length;
  const claimed = ULTRA_CHASE_CARDS.filter((card) => card.claimed).length;
  return { total, claimed, remaining: total - claimed };
}

// ------------------------------------------------------------ Pack Inventory

export type PackKind = "free" | "exchange" | "event";

export type InventoryPack = {
  id: string;
  name: string;
  kind: PackKind;
  /** 개봉 목적지 — lib/renewal/oripa.ts ORIPA_PACKS id와 정합(/shop/packs/:packId) */
  packId: string;
  /** 미개봉 잔여 수 (mock) */
  count: number;
  /** 획득 경로 — 미션 클레임 / 소각 교환 / 운영 지급 */
  source: string;
  acquiredAt: string;
};

export const PACK_KIND_LABEL: Record<PackKind, string> = {
  free: "Free",
  exchange: "Exchange",
  event: "Event",
};

/** TODO(server): 미개봉 팩 인벤토리 RPC — 미션 클레임(S-07)·소각 교환(S-18) 적립분 */
export const MOCK_INVENTORY_PACKS: InventoryPack[] = [
  {
    id: "inv-1",
    name: "Daily Free Pack",
    kind: "free",
    packId: "starter",
    count: 3,
    source: "Mission claim",
    acquiredAt: "Aug 10, 2026",
  },
  {
    id: "inv-2",
    name: "Burn & Exchange Pack",
    kind: "exchange",
    packId: "collector",
    count: 1,
    source: "Burn exchange",
    acquiredAt: "Aug 8, 2026",
  },
  {
    id: "inv-3",
    name: "Launch Week Event Pack",
    kind: "event",
    packId: "premium",
    count: 2,
    source: "Operations grant",
    acquiredAt: "Aug 5, 2026",
  },
];
